/**
 * Ways to wrap a cache source, the `getCached(path)` reader a live-tree or layout walk takes. A
 * null answer is a file that is loading or has failed, which a walk reads as not cached.
 */
/**
 * A source that holds nothing, for a walk with no loader: every path reads as never requested.
 *
 * @canonical A `getCached` reader for a walk
 */
export const NOTHING_CACHED = { getCached: () => undefined };
/**
 * `source`, with each path it is asked for handed to `onRead` with the answer it gave.
 *
 * @canonical A `getCached` reader for a walk
 */
export function recordingReads(source, onRead) {
    return {
        getCached: (path) => {
            const answer = source.getCached(path);
            onRead(path, answer);
            return answer;
        },
    };
}
/**
 * A cache of a value for each key object and variant, which `build` makes over recording copies of
 * `sources`. A later ask gets the kept value while each read the build made still answers the
 * same in the sources of that ask, so a value outlives the snapshot it was built over.
 *
 * @canonical A value kept while every cache read it made answers the same
 */
export function keptWhileAnswered({ sameVariant, perVariant, }) {
    /** Written only by the returned lookup. The entries clear when nothing holds the key any more. */
    const kept = new WeakMap();
    return (key, variant, sources, build) => {
        const stored = kept.get(key) ?? [];
        const known = stored.find((entry) => sameVariant(entry.variant, variant) && stillAnswered(entry.reads, sources));
        if (known)
            return known.value;
        const same = stored.filter((entry) => sameVariant(entry.variant, variant));
        const reads = new Map();
        const recording = Object.fromEntries(Object.entries(sources).map(([name, source]) => {
            const answers = new Map();
            reads.set(name, answers);
            return [name, recordingReads(source, (path, answer) => answers.set(path, answer))];
        }));
        const value = build(recording);
        const others = stored.filter((entry) => !same.includes(entry));
        const younger = same.slice(Math.max(0, same.length - (perVariant - 1)));
        kept.set(key, [...others, ...younger, { variant, reads, value }]);
        return value;
    };
}
function stillAnswered(reads, sources) {
    for (const [name, answers] of reads) {
        for (const [path, answer] of answers) {
            if (sources[name].getCached(path) !== answer)
                return false;
        }
    }
    return true;
}
/**
 * `source`, asked once for each path: a later ask gets the first answer, a missing one included.
 * It holds one snapshot of a cache that changes in place.
 *
 * @canonical A `getCached` reader for a walk
 */
export function answeredOnce(source) {
    const answers = new Map();
    return {
        getCached: (path) => {
            const known = answers.get(path);
            if (known !== undefined || answers.has(path))
                return known;
            const answer = source.getCached(path);
            answers.set(path, answer);
            return answer;
        },
    };
}
//# sourceMappingURL=cacheSources.js.map